import { $, _, Backbone } from "js/vendor";
import { getStaticUrl, buildNumber, isElectron } from "js/config";
import { app } from "js/namespaces";
import swHelper from "js/core/utilities/serviceWorkerHelper";
import { StyleSheet } from "js/core/styleSheet";
import { PaintStyle, ShapeType, ShapeColorStyle, IconStyle, ThemeStyleWeight, PaletteColorType, PREDEFINED_PALETTES } from "common/constants";
import logger from "js/core/logger";
import { formatter } from "js/core/utilities/formatter";
import ReferenceCollection from "js/core/storage/referenceCollection";
import StorageModel from "../storage/storageModel";
import { themes, THEME_DEFAULTS } from "common/themes";

// presentation title styles
const styles = ["bar_left", "bar_top", "block", "border", "underline_center", "underline_left", "plain"];
const preloadCache = {};

export function getThemeUrl(path, style) {
    let url = path ? `/themes/${path}/${style}.scss` : `/themes/${style}.scss`;
    return getStaticUrl(url);
}

const Theme = StorageModel.extend({

    root: "user_themes",

    defaults: THEME_DEFAULTS,

    getColors: function() {
        let themeColors = this.get("colors");
        if (!themeColors) {
            let palette = this.getPalette(this.get("palette_name"));
            themeColors = _.clone(palette.colors);
        }
        return themeColors;
    },

    getAccentColorsCount() {
        return Object.keys(this.palette.getAccentColors()).length;
    },

    getPalette: function(paletteId) {
        let palette = _.find(PREDEFINED_PALETTES, { id: paletteId });
        if (!palette) {
            // fallback for old theme support
            palette = _.find(PREDEFINED_PALETTES, { old_id: paletteId });
        }
        if (!palette) {
            palette = _.find(PREDEFINED_PALETTES, { id: "colorful" });
        }
        return _.clone(palette);
    },

    initialize: function() {
        Object.defineProperty(this, "palette", {
            get: function() {
                return this.styleSheet.palette;
            }
        });

        if (!this.get("originalId")) {
            this.set("originalId", this.id || "custom");
        }

        this.migrate();
    },

    migrate() {
        // February 2018 migration to split styleBackground options into defaultBackgroundColor
        const styleBackground = this.get("styleBackground");
        const defaultBackgroundColor = this.get("defaultBackgroundColor");
        const removedStyleBackgrounds = {
            "light": PaletteColorType.BACKGROUND_LIGHT,
            "dark": PaletteColorType.BACKGROUND_DARK,
            "color": PaletteColorType.THEME
        };

        if (["gradient", "reverseGradient"].includes(styleBackground) && !defaultBackgroundColor) {
            this.set("defaultBackgroundColor", "theme");
        } else if (removedStyleBackgrounds[styleBackground]) {
            let defaultBackground = removedStyleBackgrounds[styleBackground];
            this.set({
                "defaultBackgroundColor": defaultBackground,
                "styleBackground": "none"
            });
        }
    },

    isBespoke: function() {
        return this.has("styleSheet");
    },

    preload: function() {
        let urls;
        if (this.has("customStyles")) {
            urls = [
                getThemeUrl(null, "default"),
                getThemeUrl(this.get("customStyles"), this.get("customStyles")),
                getThemeUrl(this.get("customStyles"), "variation_" + this.get("variation"))
            ];
        } else {
            const styleUrls = styles.map(style => {
                return getThemeUrl("variations/PresentationTitle", style);
            });
            urls = [
                getThemeUrl(null, "default"),
                getThemeUrl("backgrounds", this.get("styleBackground")),
                getThemeUrl("decorations", this.get("styleDecoration")),
                getThemeUrl("elements", "default"),
                getThemeUrl("elements", "fillAndStroke"),
                getThemeUrl("elements", "filled"),
                getThemeUrl("elements", "muted"),
                getThemeUrl("elements", "outlined"),
                this.get("styleShape") == ShapeType.NONE ? getThemeUrl("shapes", "shape-none") : getThemeUrl("shapes", "shape"),
                getThemeUrl("weights", this.get("styleWeight")),
                getThemeUrl("fonts", `${this.get("styleTitleFont")}-header-${this.get("styleFontWeight")}`),
                getThemeUrl("fonts", `${this.get("styleTitleFont")}-title-${this.get("styleFontWeight")}`),
                getThemeUrl("fonts", `${this.get("styleBodyFont")}-body`),
                getThemeUrl("effects", this.get("styleEffect"))
            ].concat(styleUrls);
            console.log("PRELOAD URLS =", urls);
        }

        urls = urls.filter(url => {
            if (preloadCache[url]) {
                return false;
            }
            preloadCache[url] = true;
            return true;
        });

        if (urls.length === 0) {
            return Promise.resolve();
        }

        return swHelper.sendMessage("updateCache", urls);
    },

    async loadTheme(textToLoad = "a") {
        if (this.has("customStyles")) {
            // load bespoke stylesheet

            return this.buildStyleSheet(null, getThemeUrl(null, "default"))
                .then(styleSheet => {
                    return this.buildStyleSheet(styleSheet, getThemeUrl(this.get("customStyles"), this.get("customStyles")));
                }).then(styleSheet => {
                    return this.buildStyleSheet(styleSheet, getThemeUrl(this.get("customStyles"), `variation_${this.get("variation")}`));
                }).then(styleSheet => {
                    this.styleSheet = styleSheet;
                    this.styleSheet.applyStyleSheetVariables(this.styleSheet.variables);
                    this.styleSheet.processFillAndStroke();

                    this.styleSheet.createFilters();

                    return app.fontManager.loadFontsForTheme(this, textToLoad);
                });
        } else {
            this.decorationStyles = {
                outlined: await this.loadStyleSheet(getThemeUrl("elements", "outlined")),
                muted: await this.loadStyleSheet(getThemeUrl("elements", "muted")),
                filled: await this.loadStyleSheet(getThemeUrl("elements", "filled")),
                fillAndStroke: await this.loadStyleSheet(getThemeUrl("elements", "fillAndStroke")),
            };

            // load frankenstein stylesheet from theme properties
            return this.buildStyleSheet(null, getThemeUrl(null, "default"))
                .then(styleSheet => {
                    return this.buildStyleSheet(styleSheet, getThemeUrl("backgrounds", this.get("styleBackground")));
                }).then(styleSheet => {
                    let decoration = this.get("styleDecoration");
                    return this.buildStyleSheet(styleSheet, getThemeUrl("decorations", decoration));
                }).then(styleSheet => {
                    return this.buildStyleSheet(styleSheet, getThemeUrl("elements", this.get("styleElementStyle")));
                }).then(styleSheet => {
                    if (this.get("styleShape") == ShapeType.NONE) {
                        return this.buildStyleSheet(styleSheet, getThemeUrl("shapes", "shape-none"));
                    } else {
                        return this.buildStyleSheet(styleSheet, getThemeUrl("shapes", "shape"));
                    }
                }).then(styleSheet => {
                    return this.buildStyleSheet(styleSheet, getThemeUrl("weights", this.get("styleWeight")));
                }).then(styleSheet => {
                    return this.loadFonts(styleSheet);
                }).then(styleSheet => {
                    return this.buildStyleSheet(styleSheet, getThemeUrl("effects", this.get("styleEffect")));
                }).then(styleSheet => {
                    this.styleSheet = styleSheet;
                    this.styleSheet.applyStyleSheetVariables(this.styleSheet.variables);
                    this.styleSheet.processFillAndStroke();

                    this.styleSheet.createFilters();

                    // apply any variables to the values in the decorationStyles
                    for (let style of Object.keys(this.decorationStyles)) {
                        let decorationStyle = this.decorationStyles[style];
                        for (let property of Object.keys(decorationStyle)) {
                            let value = decorationStyle[property];
                            if (value && typeof (value) == "string" && value.startsWith("$")) {
                                let varName = value.split(" ")[0];
                                decorationStyle[property] = decorationStyle[property].replace(varName, this.styleSheet.variables[varName.substr(1)]);
                            }
                        }
                    }

                    return app.fontManager.loadFontsForTheme(this, textToLoad);
                });
        }
    },

    async loadFonts(styleSheet) {
        // handle custom font migration by loading stylesheet and saving to theme, then building stylesheet from theme
        if (!this.get("fontHeader") || !this.get("fontHeaderRegular")) {
            const headerFontPath = `${this.get("styleTitleFont")}-header-${this.get("styleFontWeight")}`;
            styleSheet = await this.buildStyleSheet(styleSheet, getThemeUrl("fonts", headerFontPath));
            this.setFontVariables("header", headerFontPath, styleSheet.variables);
        } else {
            styleSheet = this.mergeStylesheet(styleSheet, this.buildCustomFontStyleSheet("header", "Header"));
            styleSheet = this.mergeStylesheet(styleSheet, this.buildCustomFontStyleSheet("display", "Header"));
        }

        if (!this.get("fontTitle") || !this.get("fontTitleRegular")) {
            let titleFont = this.get("styleTitleFont");
            // special case for existing montserrat themes which actually use playfair as title font
            if (titleFont === "playfair") {
                titleFont = "raleway";
            }
            const titleFontPath = `${titleFont}-title-${this.get("styleFontWeight")}`;
            styleSheet = await this.buildStyleSheet(styleSheet, getThemeUrl("fonts", titleFontPath));
            this.setFontVariables("title", titleFontPath, styleSheet.variables);
        } else {
            styleSheet = this.mergeStylesheet(styleSheet, this.buildCustomFontStyleSheet("title", "Title"));
        }

        if (!this.get("fontBody") || !this.get("fontBodyRegular")) {
            const bodyFontPath = `${this.get("styleBodyFont")}-body`;
            styleSheet = await this.buildStyleSheet(styleSheet, getThemeUrl("fonts", bodyFontPath));
            this.setFontVariables("body", bodyFontPath, styleSheet.variables);
        } else {
            styleSheet = this.mergeStylesheet(styleSheet, this.buildCustomFontStyleSheet("body", "Body"));
        }
        return styleSheet;
    },

    setFontVariables(fontType, fontFamily, variables) {
        const capitalizedFontType = formatter.capitalizeFirstLetter(fontType);
        const fontUpdates = {
            [`font${capitalizedFontType}`]: fontFamily,
            [`font${capitalizedFontType}Regular`]: variables[`${fontType}FontRegular`],
            [`font${capitalizedFontType}Italic`]: variables[`${fontType}FontItalic`],
            [`font${capitalizedFontType}Emphasized`]: variables[`${fontType}FontEmphasized`],
            [`font${capitalizedFontType}EmphasizedItalic`]: variables[`${fontType}FontEmphasizedItalic`],
            [`font${capitalizedFontType}LineHeight`]: variables[`${fontType}FontLineHeight`],
            [`font${capitalizedFontType}LetterSpacing`]: variables[`${fontType}FontLetterSpacing`],
            [`font${capitalizedFontType}TextTransform`]: variables[`${fontType}FontTextTransform`] || "auto",
            [`font${capitalizedFontType}Scaling`]: variables[`${fontType}FontScaling`] || 100
        };

        if (variables[`${fontType}FontStrokeThickness`]) {
            fontUpdates[`font${capitalizedFontType}StrokeThickness`] = variables[`${fontType}FontStrokeThickness`];
        }
        this.set(fontUpdates);
    },

    buildCustomFontStyleSheet(fontKey, themeKey) {
        return `
$${fontKey}FontRegular: ${this.get(`font${themeKey}Regular`)};
$${fontKey}FontItalic: ${this.get(`font${themeKey}Italic`)};
$${fontKey}FontEmphasized: ${this.get(`font${themeKey}Emphasized`)};
$${fontKey}FontEmphasizedItalic: ${this.get(`font${themeKey}EmphasizedItalic`)};

$${fontKey}FontLineHeight: ${this.get(`font${themeKey}LineHeight`)};
$${fontKey}FontLetterSpacing: ${this.get(`font${themeKey}LetterSpacing`)};
$${fontKey}FontTextTransform: ${this.get(`font${themeKey}TextTransform`)};
$${fontKey}FontScaling: ${this.get(`font${themeKey}Scaling`)};
$${fontKey}FontStrokeThickness: ${this.get(`font${themeKey}StrokeThickness`)};
`;
    },

    buildStyleSheet: function(styleSheet, file, metadata) {
        return fetch(file).then(response => {
            return response.text();
        }).then(css => {
            return this.mergeStylesheet(styleSheet, css, metadata);
        });
    },

    async loadStyleSheet(file) {
        return fetch(file).then(response => {
            return response.text();
        }).then(css => {
            return new StyleSheet(this, css);
        });
    },

    mergeStylesheet(styleSheet, css, metadata) {
        if (!_.isEmpty(css)) {
            if (styleSheet) {
                let buildStyles = new StyleSheet(this, css);
                _.merge(styleSheet, buildStyles);
            } else {
                styleSheet = new StyleSheet(this, css);
            }
        }
        if (metadata) {
            _.extend(styleSheet, metadata);
        }
        return styleSheet;
    },

    sync: function() {
        //void
    }
});

const UserThemes = ReferenceCollection.extend({
    model: Theme,
    referenceRoot: "users",
    getReferenceId: function() {
        return `${this.userId}/user_themes`;
    },

    initialize() {
        this.type = "Themes";
        this.userId = app.user.id;
    },
    saveUserTheme: function(theme, name, orgId) {
        let existingTheme;
        if (orgId) {
            //when saving a theme from org context then check for theme with given name and orgId
            existingTheme = this.find({ name, orgId });
        } else {
            //when saving a theme from personal context then check for theme with given name and NO orgId
            const theme = this.find({ name });
            if (theme && !theme.has("orgId")) {
                existingTheme = theme;
            }
        }
        if (existingTheme) {
            existingTheme.update(Object.assign({}, theme.attributes, { isBuiltIn: false, name }));
            logger.info("Updated existing theme " + existingTheme.id);
            return existingTheme;
        } else {
            let data = {
                ...theme.attributes,
                name: name,
                userId: app.user.id,
                isBuiltIn: false,
                orgId
            };
            delete data.id;
            let newTheme = new Theme(data);
            newTheme = this.add(newTheme);
            logger.info("Created new theme " + newTheme.id);
            return newTheme;
        }
    }
});

const BuiltInThemes = Backbone.Collection.extend({
    model: Theme,

    initialize: function() {
        Object.values(themes).forEach(theme => {
            this.add(new Theme(theme, { disconnected: true, autoLoad: false }));
        });
    }
});

export {
    ShapeType, PaintStyle, ShapeColorStyle, IconStyle, ThemeStyleWeight, PREDEFINED_PALETTES,
    Theme, UserThemes, BuiltInThemes
};

